import { doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';

/** Quotas offre Basic (1 plan, 1 catégorie de tags, 100 trades visibles). */
export const BASIC_TIER_PLAN_LIMIT = 1;
export const BASIC_TIER_TAG_CATEGORY_LIMIT = 1;
export const BASIC_TIER_TRADE_LIMIT = 100;

export const BASIC_TIER_TRADE_BANNER_SOFT = 50;
export const BASIC_TIER_TRADE_BANNER_WARNING = 75;

export type BasicTradeLimitBannerLevel = 'soft' | 'warning' | 'limit';

export type TradeWithDates = {
  openDateTime?: string | Date | null;
  closeDateTime?: string | Date | null;
};

let cachedLimitedTier: { userId: string; value: boolean } | null = null;
let authBasicTierLimitedHint: boolean | null = null;
const basicTierLockRevisionListeners = new Set<() => void>();

export function clearBasicTierCache(): void {
  cachedLimitedTier = null;
}

/** Hint AuthContext (0 lecture Firestore) — utilisé par accountService au lieu de relire users/. */
export function setAuthBasicTierLimitedHint(value: boolean | null): void {
  if (authBasicTierLimitedHint === value) return;
  authBasicTierLimitedHint = value;
  basicTierLockRevisionListeners.forEach((listener) => listener());
}

export function getAuthBasicTierLimitedHint(): boolean | null {
  return authBasicTierLimitedHint;
}

/** Notifie les abonnements comptes existants quand le tier change (sans nouveau listener Firestore). */
export function subscribeBasicTierLockRevision(listener: () => void): () => void {
  basicTierLockRevisionListeners.add(listener);
  return () => {
    basicTierLockRevisionListeners.delete(listener);
  };
}

export function isBasicLimitedFromAuthState(
  subscriptionType: string,
  isPremium: boolean,
  isTrial: boolean,
  isBetatesteur: boolean,
): boolean {
  if (isBetatesteur) return false;

  const sub = subscriptionType.toLowerCase();
  if (sub.includes('basic')) return true;
  if (isTrial && !isPremium) return true;

  return false;
}

export function isBasicLimitedUserData(data: Record<string, unknown> | undefined | null): boolean {
  if (!data) return false;

  if (data.isLifetime === true || data.isLifetime === 'true') return false;
  if (data.isBetatesteur === true || data.isBetatesteur === 'true') return false;

  const subscriptionType = String(data.subscriptionType || '').toLowerCase();
  const isPremium = data.isPremium === true || data.isPremium === 'true';
  const isTrial = data.isTrial === true || data.isTrial === 'true';

  if (subscriptionType.includes('basic')) return true;
  if (isTrial && !isPremium) return true;

  return false;
}

export async function resolveIsBasicLimitedTier(userId?: string): Promise<boolean> {
  const auth = getAuth();
  const uid = userId || auth.currentUser?.uid;
  if (!uid) return false;

  if (cachedLimitedTier?.userId === uid) {
    return cachedLimitedTier.value;
  }

  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    const data = userDoc.data() || {};
    const value = isBasicLimitedUserData(data);
    cachedLimitedTier = { userId: uid, value };
    return value;
  } catch {
    return false;
  }
}

function normalizeTradeTimestamp(value: unknown): number {
  if (!value) return 0;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }

  if (typeof value === 'object' && value !== null) {
    if ('toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const parsed = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }
    if ('seconds' in value && typeof (value as { seconds?: number }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000;
    }
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function sortTradesByRecency<T extends TradeWithDates>(trades: T[]): T[] {
  return [...trades].sort((a, b) => {
    const dateA = normalizeTradeTimestamp(a.closeDateTime) || normalizeTradeTimestamp(a.openDateTime);
    const dateB = normalizeTradeTimestamp(b.closeDateTime) || normalizeTradeTimestamp(b.openDateTime);
    return dateB - dateA;
  });
}

export function applyBasicTradeWindow<T extends TradeWithDates>(
  trades: T[],
  isLimitedTier: boolean,
): { visibleTrades: T[]; hiddenCount: number; totalCount: number } {
  const totalCount = trades.length;
  if (!isLimitedTier || totalCount <= BASIC_TIER_TRADE_LIMIT) {
    return { visibleTrades: trades, hiddenCount: 0, totalCount };
  }

  const visibleTrades = sortTradesByRecency(trades).slice(0, BASIC_TIER_TRADE_LIMIT);
  return {
    visibleTrades,
    hiddenCount: totalCount - BASIC_TIER_TRADE_LIMIT,
    totalCount,
  };
}

export function getBasicTradeLimitBannerLevel(
  totalCount: number,
  isLimitedTier: boolean,
  isSoftBannerDismissed: boolean,
): BasicTradeLimitBannerLevel | null {
  if (!isLimitedTier || totalCount < BASIC_TIER_TRADE_BANNER_SOFT) {
    return null;
  }
  if (totalCount >= BASIC_TIER_TRADE_LIMIT) {
    return 'limit';
  }
  if (totalCount >= BASIC_TIER_TRADE_BANNER_WARNING) {
    return 'warning';
  }
  if (!isSoftBannerDismissed) {
    return 'soft';
  }
  return null;
}

export function getBasicTradeLimitDismissStorageKey(userId: string): string {
  return `focuspips_basic_trade_banner_50_dismissed_${userId}`;
}
